import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hammer, Home, Wrench, Camera, Ruler, Loader2, LogOut, Sparkles, MapPin, DollarSign, Clock, Star, CheckCircle2, XCircle, ChevronRight, ArrowLeft, Plus, Trash2, Upload, X, Zap, Send, Info, Award, Mic } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";

/**
 * Fliboe Contractors — home-improvement PWA. Isolated route tree at
 * /contractors/*, own token, own landing. Reuses main /api/auth/* for login.
 *
 * Two roles share one auth surface:
 *   • Homeowner: describe the problem → Fliboe AI writes a diagnosis + fair
 *     price band → post the job → quotes race in → pick a winner → review.
 *   • Pro: onboard with trades + service ZIPs → browse open jobs → quote →
 *     get picked → complete → collect a review.
 *
 * Includes a kid-friendly Digital Measure Tape (screen ruler + photo-scale
 * measure) — pure client-side, no LLM, no backend cost.
 */
const CT_TOKEN_KEY = "fliboe_contractors_token";
const authHeaders = () => { const t = localStorage.getItem(CT_TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };

// ────────────────────────────────────────────────────────────
// Shared shell
// ────────────────────────────────────────────────────────────
function Shell({ children, signedIn = false, onSignOut }) {
  return (
    <main className="min-h-screen bg-[#0b1220] text-white relative" data-testid="contractors-shell">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/60 via-[#0b1220]/85 to-[#0b1220]" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(16,185,129,0.18), transparent 60%), radial-gradient(ellipse at bottom right, rgba(56,189,248,0.10), transparent 60%)" }} />
      </div>
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/contractors" className="flex items-center gap-2 group" data-testid="ct-brand">
            <Hammer className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300" />
            <span className="fl-display text-lg font-black tracking-tight text-white">Fixo <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest ml-1">by Fliboe</span></span>
          </Link>
          <div className="flex items-center gap-2">
            {signedIn ? (
              <button onClick={onSignOut} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="ct-signout"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
            ) : (
              <Link to="/contractors/login" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="ct-header-signin">Sign in</Link>
            )}
          </div>
        </div>
      </header>
      <div className="relative z-10">{children}</div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// LANDING (/contractors)
// ────────────────────────────────────────────────────────────
export function ContractorsLanding() {
  return (
    <Shell>
      <section className="max-w-5xl mx-auto px-5 pt-16 pb-20 sm:pt-24" data-testid="ct-landing">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-4">// Fixo by Fliboe · Home repair, done right</div>
        <h1 className="fl-display text-5xl sm:text-7xl font-black tracking-tighter leading-[0.9]">
          Snap it.<br />
          <span className="text-emerald-400">Price it.</span> Hire it.
        </h1>
        <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
          Describe your home repair in plain words. Get an <strong className="text-white">AI diagnosis</strong>, a <strong className="text-white">fair-price band</strong>, and a <strong className="text-white">DIY-vs-hire verdict</strong> in 20 seconds. If it needs a pro, local contractors race to send you binding quotes.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to="/contractors/dashboard" className="fl-btn !bg-emerald-500 hover:!bg-emerald-400 !text-black" data-testid="ct-landing-start">
            <Zap className="w-4 h-4" /> Get an instant estimate
          </Link>
          <Link to="/contractors/login" className="fl-btn ghost" data-testid="ct-landing-signin">Sign in as a pro</Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[
            { icon: Sparkles, title: "AI diagnosis", body: "Describe the problem in text. Our AI writes the plain-English fix, price band, and DIY verdict — no jargon." },
            { icon: DollarSign, title: "Quotes chase you", body: "Post the job once. Vetted local pros in your ZIP code send binding quotes. You pick the winner." },
            { icon: Ruler,     title: "Project measure tape", body: "Voice-powered measurement log for every room. Say the number, we save it. Photo-scale mode for real-world dimensions. Zero hardware." },
          ].map((f, i) => (
            <div key={i} className="fl-card p-5 bg-white/[0.02] border-white/5">
              <f.icon className="w-5 h-5 text-emerald-400" />
              <h3 className="mt-3 fl-display text-lg font-bold text-white">{f.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-xs fl-mono uppercase tracking-widest text-white/40">
          Free for homeowners · Pros keep 100% until we launch escrow
        </div>
      </section>

      <FixoContactForm />
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────
// Contact form (below the fold on the Fixo landing)
// ────────────────────────────────────────────────────────────
function FixoContactForm() {
  const [f, setF] = useState({ name: "", email: "", role: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name.trim() || !f.email.trim() || f.message.trim().length < 5) {
      setErr("Name, email, and a short message are required."); return;
    }
    setBusy(true);
    try { await api.post("/contractors/contact", f); setSent(true); }
    catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : "Could not send. Try again."));
    }
    finally { setBusy(false); }
  };
  return (
    <section className="max-w-3xl mx-auto px-5 pb-24" data-testid="ct-contact">
      <div className="fl-card p-6 bg-white/[0.02] border-emerald-400/20">
        <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// Talk to us</div>
        <h2 className="fl-display text-2xl sm:text-3xl font-black text-white mt-1">Homeowner, pro, or press — reach out.</h2>
        <p className="text-slate-400 text-sm mt-1">We read every message. Support replies within 24 hrs.</p>
        {sent ? (
          <div className="mt-5 p-4 rounded-lg bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-sm" data-testid="ct-contact-thanks">
            Thanks — got your message. We&apos;ll reply to <strong>{f.email}</strong> shortly.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3">
            <input className="fl-input" placeholder="Your name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="ct-contact-name" maxLength={120} />
            <input className="fl-input" placeholder="Email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="ct-contact-email" />
            <select className="fl-input sm:col-span-2" value={f.role} onChange={e => setF({ ...f, role: e.target.value })} data-testid="ct-contact-role">
              <option value="">I&apos;m…</option>
              <option value="homeowner">A homeowner with a question</option>
              <option value="contractor">A contractor / pro</option>
              <option value="press">Press / partnership</option>
              <option value="other">Something else</option>
            </select>
            <textarea className="fl-input sm:col-span-2 min-h-[110px]" placeholder="Your message" value={f.message} onChange={e => setF({ ...f, message: e.target.value })} data-testid="ct-contact-message" maxLength={2000} />
            {err && <div className="text-red-300 text-sm sm:col-span-2" data-testid="ct-contact-err">{err}</div>}
            <button type="submit" disabled={busy} className="fl-btn !bg-emerald-500 !text-black sm:col-span-2" data-testid="ct-contact-send">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// LOGIN (/contractors/login) — reuses main /auth/* endpoints
// ────────────────────────────────────────────────────────────
export function ContractorsLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.(); setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try { await api.post("/auth/forgot-password", { email }); setForgotOk(true); }
      catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!email.trim() || password.length < 6) { setErr("Email + 6-char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token");
      localStorage.setItem(CT_TOKEN_KEY, data.session_token);
      navigate("/contractors/dashboard");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Sign-in failed."); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <section className="max-w-md mx-auto px-5 py-16" data-testid="ct-login">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-3 text-center">// Fixo by Fliboe</div>
        <h1 className="fl-display text-3xl font-black text-center">
          {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in to Fixo"}
        </h1>
        <form onSubmit={submit} className="fl-card p-6 mt-6 space-y-3 bg-black/60 border-emerald-400/20">
          {mode === "forgot" && forgotOk ? (
            <div className="text-emerald-300 text-sm text-center py-4" data-testid="ct-forgot-ok">
              If <strong>{email}</strong> matches an account, we&apos;ve sent a reset link.
              <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-emerald-300 text-xs hover:underline">Back to sign in</button>
            </div>
          ) : (
            <>
              {mode === "signup" && (
                <input className="fl-input w-full" placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="ct-login-name" />
              )}
              <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="ct-login-email" />
              {mode !== "forgot" && (
                <input className="fl-input w-full" placeholder="Password (6+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="ct-login-password" />
              )}
              {err && <div className="text-red-400 text-sm" data-testid="ct-login-err">{err}</div>}
              <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-emerald-500 hover:!bg-emerald-400 !text-black" data-testid="ct-login-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
                {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset email" : "Sign in"}
              </button>
              {mode !== "forgot" && (
                <>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button
                    type="button"
                    onClick={() => { const redirectUrl = window.location.origin + "/contractors/auth-callback"; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`; }}
                    className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition"
                    data-testid="ct-login-google"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Continue with Google
                  </button>
                </>
              )}
              <div className="flex items-center justify-between gap-3 mt-2">
                <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }} className="text-emerald-300 text-xs hover:underline">
                  {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
                </button>
                {mode !== "forgot" && (
                  <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-emerald-300 hover:underline">Forgot password?</button>
                )}
              </div>
            </>
          )}
        </form>
      </section>
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────
// GOOGLE AUTH CALLBACK
// ────────────────────────────────────────────────────────────
export function ContractorsAuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return; processed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) { navigate("/contractors/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("No token");
        localStorage.setItem(CT_TOKEN_KEY, data.session_token);
        window.history.replaceState({}, document.title, "/contractors/dashboard");
        navigate("/contractors/dashboard", { replace: true });
      } catch (_) { navigate("/contractors/login", { replace: true }); }
    })();
  }, [navigate]);
  return <Shell><div className="text-emerald-300 fl-mono text-xs uppercase tracking-widest text-center py-24" data-testid="ct-auth-callback">// Signing you in…</div></Shell>;
}

// ────────────────────────────────────────────────────────────
// DASHBOARD (/contractors/dashboard) — role-aware
// ────────────────────────────────────────────────────────────
export function ContractorsDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const signOut = () => { localStorage.removeItem(CT_TOKEN_KEY); navigate("/contractors"); };

  const load = async () => {
    try {
      const { data } = await api.get("/contractors/me", { headers: authHeaders() });
      setMe(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/contractors/login");
      else setErr(e?.response?.data?.detail || "Could not load your account.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem(CT_TOKEN_KEY)) { navigate("/contractors/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Shell signedIn onSignOut={signOut}><div className="text-emerald-300 fl-mono text-xs uppercase tracking-widest text-center py-24" data-testid="ct-loading">// Loading your workspace…</div></Shell>;

  const role = me?.user?.role;

  return (
    <Shell signedIn onSignOut={signOut}>
      <section className="max-w-5xl mx-auto px-5 py-8 space-y-6" data-testid="ct-dashboard">
        {err && <div className="fl-card p-4 border-red-500/40 bg-red-950/40 text-red-200 text-sm">{err}</div>}
        {!role ? (
          <RolePicker onDone={load} defaultName={me?.user?.name} defaultEmail={me?.user?.email} />
        ) : role === "homeowner" ? (
          <HomeownerHome me={me} onReload={load} />
        ) : (
          <ProHome me={me} onReload={load} />
        )}
      </section>
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────
// Role picker
// ────────────────────────────────────────────────────────────
function RolePicker({ onDone, defaultName, defaultEmail }) {
  const [role, setRole] = useState("");
  return (
    <div className="fl-card p-8 bg-white/[0.02] border-emerald-400/20" data-testid="ct-rolepicker">
      <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// Welcome</div>
      <h2 className="fl-display text-3xl font-black text-white mt-2">Are you fixing your home, or fixing homes?</h2>
      <p className="text-slate-400 text-sm mt-2">Pick one. You can switch later if you wear both hats.</p>
      {!role ? (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <button onClick={() => setRole("homeowner")} className="fl-card p-6 text-left hover:border-emerald-400 hover:bg-emerald-500/5 transition" data-testid="ct-role-homeowner">
            <Home className="w-8 h-8 text-emerald-400" />
            <h3 className="fl-display text-xl font-bold text-white mt-3">I&apos;m a homeowner</h3>
            <p className="text-slate-400 text-sm mt-1">Diagnose problems, get fair prices, hire local pros.</p>
          </button>
          <button onClick={() => setRole("pro")} className="fl-card p-6 text-left hover:border-emerald-400 hover:bg-emerald-500/5 transition" data-testid="ct-role-pro">
            <Wrench className="w-8 h-8 text-emerald-400" />
            <h3 className="fl-display text-xl font-bold text-white mt-3">I&apos;m a pro</h3>
            <p className="text-slate-400 text-sm mt-1">Browse jobs in my ZIP codes, send quotes, get hired.</p>
          </button>
        </div>
      ) : role === "homeowner" ? (
        <HomeownerOnboard defaultName={defaultName} defaultEmail={defaultEmail} onDone={onDone} onBack={() => setRole("")} />
      ) : (
        <ProOnboard defaultName={defaultName} defaultEmail={defaultEmail} onDone={onDone} onBack={() => setRole("")} />
      )}
    </div>
  );
}

function HomeownerOnboard({ defaultName, onDone, onBack }) {
  const [name, setName] = useState(defaultName || "");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e?.preventDefault?.(); setErr(""); setBusy(true);
    try { await api.post("/contractors/onboard", { role: "homeowner", display_name: name, zip_code: zip }, { headers: authHeaders() }); onDone?.(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Sign-up failed."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="ct-homeowner-onboard">
      <button type="button" onClick={onBack} className="text-xs text-emerald-300 hover:underline flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> back</button>
      <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Your name</span>
        <input className="fl-input w-full mt-1" value={name} onChange={e => setName(e.target.value)} data-testid="ct-onboard-home-name" /></label>
      <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">ZIP code (where you live)</span>
        <input className="fl-input w-full mt-1" value={zip} onChange={e => setZip(e.target.value)} placeholder="30303" required data-testid="ct-onboard-home-zip" /></label>
      {err && <div className="text-red-300 text-sm">{err}</div>}
      <button type="submit" disabled={busy} className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="ct-onboard-home-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Home className="w-4 h-4" />} Start using Fixo</button>
    </form>
  );
}

function ProOnboard({ defaultName, onDone, onBack }) {
  const [name, setName] = useState(defaultName || "");
  const [biz, setBiz] = useState("");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [zip, setZip] = useState("");
  const [zips, setZips] = useState([]);
  const [trades, setTrades] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/contractors/config").then(({ data }) => setAllTrades(data.trades || [])); }, []);

  const addZip = () => { const z = zip.trim(); if (z && !zips.includes(z)) setZips([...zips, z]); setZip(""); };
  const toggleTrade = (id) => setTrades(t => t.includes(id) ? t.filter(x => x !== id) : [...t, id]);

  const submit = async (e) => {
    e?.preventDefault?.(); setErr("");
    if (!trades.length) { setErr("Pick at least one trade."); return; }
    if (!zips.length) { setErr("Add at least one service ZIP."); return; }
    setBusy(true);
    try {
      await api.post("/contractors/onboard", { role: "pro", display_name: name, business_name: biz, phone, bio, trades, service_zips: zips }, { headers: authHeaders() });
      onDone?.();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Sign-up failed."); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="ct-pro-onboard">
      <button type="button" onClick={onBack} className="text-xs text-emerald-300 hover:underline flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> back</button>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Your name</span><input className="fl-input w-full mt-1" value={name} onChange={e => setName(e.target.value)} data-testid="ct-onboard-pro-name" /></label>
        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Business name (optional)</span><input className="fl-input w-full mt-1" value={biz} onChange={e => setBiz(e.target.value)} data-testid="ct-onboard-pro-biz" /></label>
        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Phone (optional)</span><input className="fl-input w-full mt-1" value={phone} onChange={e => setPhone(e.target.value)} data-testid="ct-onboard-pro-phone" /></label>
        <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Short bio (optional)</span><input className="fl-input w-full mt-1" value={bio} onChange={e => setBio(e.target.value)} maxLength={200} placeholder="15 years fixing sinks in Atlanta." data-testid="ct-onboard-pro-bio" /></label>
      </div>
      <div>
        <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Your trades (pick all that apply)</span>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ct-onboard-pro-trades">
          {allTrades.map(t => (
            <button type="button" key={t.id} onClick={() => toggleTrade(t.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${trades.includes(t.id) ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`ct-trade-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <div>
        <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Service ZIP codes (jobs in these will show)</span>
        <div className="mt-1 flex items-center gap-2">
          <input className="fl-input flex-1" value={zip} onChange={e => setZip(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addZip(); } }} placeholder="30303" data-testid="ct-onboard-pro-zip" />
          <button type="button" onClick={addZip} className="fl-btn ghost !py-2 !px-3 text-xs" data-testid="ct-onboard-pro-addzip"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        {zips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {zips.map(z => (
              <span key={z} className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-200 text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {z}
                <button type="button" onClick={() => setZips(zips.filter(x => x !== z))} className="ml-1 opacity-70 hover:opacity-100"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
      {err && <div className="text-red-300 text-sm">{err}</div>}
      <button type="submit" disabled={busy} className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="ct-onboard-pro-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} Start finding jobs</button>
    </form>
  );
}

// ────────────────────────────────────────────────────────────
// HOMEOWNER HOME — triage wizard, measure tape, my jobs
// ────────────────────────────────────────────────────────────
function HomeownerHome({ me, onReload }) {
  const [tab, setTab] = useState("fix");
  return (
    <div className="space-y-4" data-testid="ct-homeowner-home">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// {me.user.profile?.display_name || "Homeowner"} · {me.user.profile?.zip_code}</div>
          <h1 className="fl-display text-2xl font-black text-white">What&apos;s broken today?</h1>
        </div>
        <div className="text-right">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">Free AI estimates today</div>
          <div className="text-white text-sm mt-1">{me.quota.triage_used}/{me.quota.triage_limit}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="ct-homeowner-tabs">
        {[
          { id: "fix", label: "Fix something", icon: Sparkles },
          { id: "measure", label: "Measure tape", icon: Ruler },
          { id: "jobs", label: "My jobs", icon: Hammer },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${tab === t.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`ct-home-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "fix" && <TriageWizard me={me} onPosted={onReload} />}
      {tab === "measure" && <MeasureTape />}
      {tab === "jobs" && <MyJobs />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Triage wizard — description → AI estimate → post job
// ────────────────────────────────────────────────────────────
function TriageWizard({ me, onPosted }) {
  const [step, setStep] = useState("describe"); // describe | result | posted
  const [description, setDescription] = useState("");
  const [zip, setZip] = useState(me.user.profile?.zip_code || "");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [triage, setTriage] = useState(null);
  const [posted, setPosted] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "" });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData(); fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/contractors/transcribe", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" }, timeout: 60000 });
          setDescription(d => (d ? d.trim() + " " : "") + (data.text || ""));
        } catch (e) { setErr(e?.response?.data?.detail || "Voice transcribe failed."); }
        finally { setTranscribing(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { setErr("Microphone blocked. Enable mic permission and retry."); }
  };
  const stopRec = () => {
    try { mediaRef.current?.stop(); } catch (e) {}
    setRecording(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("photo", file);
      const { data } = await api.post("/contractors/photos", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setPhotos(p => [...p, data.photo_url]);
    } catch (e) { setErr(e?.response?.data?.detail || "Photo upload failed."); }
    finally { setUploading(false); }
  };

  const runTriage = async () => {
    if (description.trim().length < 10 && photos.length === 0) { setErr("Describe the problem (10+ chars) or scan a photo."); return; }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/contractors/triage", { description, zip_code: zip, photos }, { headers: authHeaders(), timeout: 90000 });
      setTriage(data); setStep("result");
    } catch (e) { setErr(e?.response?.data?.detail || "Estimate failed."); }
    finally { setBusy(false); }
  };

  const postJob = async () => {
    if (!triage) return;
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/contractors/jobs", {
        triage_id: triage.triage_id,
        description, trade: triage.diagnosis.trade,
        severity: triage.diagnosis.severity, zip_code: zip, photos,
      }, { headers: authHeaders() });
      setPosted(data); setStep("posted"); onPosted?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not post job."); }
    finally { setBusy(false); }
  };

  if (step === "describe") {
    const scanMode = photos.length > 0;
    return (
      <div className="fl-card p-6 bg-white/[0.02] border-emerald-400/20" data-testid="ct-triage-describe">
        <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// Step 1</div>
        <h2 className="fl-display text-2xl font-black text-white mt-1">Describe it, scan it, or both.</h2>
        <p className="text-slate-400 text-sm mt-1">Type a sentence, upload a photo for AI to scan, or do both for the best estimate.</p>
        <textarea
          className="fl-input w-full mt-4 min-h-[120px]"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder={scanMode ? "Optional — add context (when it started, what you've tried)…" : "Kitchen faucet is dripping from the handle even when off. Started 3 days ago."}
          maxLength={2000}
          data-testid="ct-triage-desc"
        />
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onMouseDown={startRec} onMouseUp={stopRec} onMouseLeave={() => recording && stopRec()}
            onTouchStart={(e) => { e.preventDefault(); startRec(); }} onTouchEnd={(e) => { e.preventDefault(); stopRec(); }}
            disabled={transcribing}
            className={`fl-btn !py-2 !px-3 text-xs ${recording ? "!bg-red-500 !text-white animate-pulse" : "ghost"}`}
            data-testid="ct-triage-voice"
          >
            {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            {transcribing ? "Transcribing…" : (recording ? "Recording — release to stop" : "Hold to describe with your voice")}
          </button>
          <span className="text-[10px] text-white/40 fl-mono">Whisper transcribes into the box above</span>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Your ZIP code</span><input className="fl-input w-full mt-1" value={zip} onChange={e => setZip(e.target.value)} data-testid="ct-triage-zip" /></label>
          <div>
            <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Scan photos {scanMode ? `· AI vision on (${Math.min(photos.length, 3)}/3)` : "(up to 3, optional)"}</span>
            <label className="mt-1 fl-btn ghost !py-2 !px-3 text-xs cursor-pointer inline-flex" data-testid="ct-triage-photo-btn">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : (scanMode ? "Add another photo" : "Scan a photo")}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => uploadPhoto(e.target.files?.[0])} data-testid="ct-triage-photo-input" />
            </label>
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {photos.map(p => (
                  <div key={p} className="relative w-14 h-14 rounded-lg overflow-hidden border border-white/10">
                    <img src={resolveAudio(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter(x => x !== p))} className="absolute top-0 right-0 w-5 h-5 bg-black/70 grid place-items-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {err && <div className="text-red-300 text-sm mt-3" data-testid="ct-triage-err">{err}</div>}
        <button onClick={runTriage} disabled={busy} className="fl-btn mt-4 !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="ct-triage-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? (scanMode ? "Scanning…" : "Diagnosing…") : (scanMode ? "Scan & get estimate" : "Get my instant estimate")}
        </button>
        <div className="mt-2 text-xs text-white/40 fl-mono">Free · {me.quota.triage_used}/{me.quota.triage_limit} used today · Takes ~10 seconds</div>
      </div>
    );
  }

  if (step === "result" && triage) {
    const d = triage.diagnosis;
    const sevColor = d.severity === "emergency" ? "red" : d.severity === "high" ? "amber" : d.severity === "medium" ? "orange" : "emerald";
    return (
      <div className="fl-card p-6 bg-white/[0.02] border-emerald-400/20" data-testid="ct-triage-result">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// AI Diagnosis</div>
            <h2 className="fl-display text-2xl font-black text-white capitalize mt-1">{d.trade} · <span className={`text-${sevColor}-300`}>{d.severity}</span></h2>
          </div>
          <div className="text-right">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">Fair-price band</div>
            <div className="fl-display text-2xl font-black text-emerald-300">${d.price_low}-${d.price_high}</div>
            <div className="fl-mono text-[10px] uppercase tracking-widest text-white/40">confidence: {d.confidence}</div>
          </div>
        </div>
        <p className="mt-4 text-slate-200 text-[15px] leading-relaxed" data-testid="ct-triage-summary">{d.summary}</p>
        <div className={`mt-4 p-3 rounded-xl border ${d.diy_ok ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-500/10 border-emerald-500/30"}`}>
          {d.diy_ok ? <CheckCircle2 className="w-5 h-5 text-emerald-300" /> : <XCircle className="w-5 h-5 text-emerald-300" />}
          <div className="mt-1 text-white text-sm"><strong>{d.diy_ok ? "You can DIY this." : "Hire a pro for this one."}</strong> {d.diy_note}</div>
        </div>
        {Array.isArray(d.next_steps) && d.next_steps.length > 0 && (
          <div className="mt-4">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Next steps</div>
            <ol className="mt-1 list-decimal list-inside text-slate-300 text-sm space-y-0.5">{d.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button onClick={postJob} disabled={busy} className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="ct-triage-postjob">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post this job for quotes
          </button>
          <button onClick={() => { setStep("describe"); setTriage(null); }} className="fl-btn ghost !py-2 !px-3 text-xs" data-testid="ct-triage-again">Try a different description</button>
        </div>
        {err && <div className="text-red-300 text-sm mt-3">{err}</div>}
      </div>
    );
  }

  return (
    <div className="fl-card p-6 bg-emerald-500/10 border-emerald-500/30" data-testid="ct-triage-posted">
      <CheckCircle2 className="w-6 h-6 text-emerald-300" />
      <h2 className="fl-display text-2xl font-black text-white mt-2">Job posted. Local pros are being notified.</h2>
      <p className="text-slate-300 text-sm mt-1">Quotes usually start arriving within an hour. Check the &quot;My jobs&quot; tab to review and pick.</p>
      <button onClick={() => { setStep("describe"); setTriage(null); setDescription(""); setPhotos([]); setPosted(null); }} className="fl-btn mt-4 ghost !py-2 !px-3 text-xs">Diagnose another problem</button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// My jobs — homeowner view of their posts + quotes
// ────────────────────────────────────────────────────────────
function MyJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/contractors/jobs/mine", { headers: authHeaders() }); setJobs(data.jobs || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const accept = async (job, quoteId) => {
    setErr(""); setBusy(quoteId);
    try { await api.post(`/contractors/jobs/${job.job_id}/accept-quote`, { quote_id: quoteId }, { headers: authHeaders() }); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not accept quote."); }
    finally { setBusy(""); }
  };
  const complete = async (job) => {
    if (!window.confirm("Mark this job complete? You'll be able to leave a review after.")) return;
    setErr(""); setBusy(job.job_id);
    try { await api.post(`/contractors/jobs/${job.job_id}/complete`, {}, { headers: authHeaders() }); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not complete."); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="text-white/40 fl-mono text-xs py-8">// Loading your jobs…</div>;
  if (!jobs.length) return <div className="fl-card p-6 bg-white/[0.02] border-white/5 text-slate-400 text-sm" data-testid="ct-jobs-empty">No jobs yet. Head to &quot;Fix something&quot; to post one.</div>;

  return (
    <div className="space-y-3" data-testid="ct-jobs-list">
      {err && <div className="text-red-300 text-sm">{err}</div>}
      {jobs.map(job => (
        <div key={job.job_id} className="fl-card p-4 bg-white/[0.02] border-white/5" data-testid={`ct-job-${job.job_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">// {job.trade} · {job.status} · {(job.quotes || []).length} quote{(job.quotes || []).length === 1 ? "" : "s"}</div>
              <div className="text-white text-sm mt-1 truncate">{job.description}</div>
            </div>
            <button onClick={() => setExpanded(expanded === job.job_id ? null : job.job_id)} className="text-xs text-emerald-300 hover:underline">{expanded === job.job_id ? "Hide" : "Open"}</button>
          </div>
          {expanded === job.job_id && (
            <div className="mt-4 border-t border-white/5 pt-4">
              {(job.quotes || []).length === 0 && <div className="text-slate-400 text-sm">No quotes yet. Pros in your ZIP have been notified.</div>}
              <div className="space-y-2">
                {(job.quotes || []).map(q => (
                  <div key={q.quote_id} className={`p-3 rounded-xl border ${job.accepted_quote_id === q.quote_id ? "bg-emerald-500/10 border-emerald-500/30" : "bg-black/40 border-white/5"}`} data-testid={`ct-quote-${q.quote_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-white font-semibold">{q.pro_name}</div>
                        <div className="text-slate-400 text-xs">{q.timeline_days} day{q.timeline_days === 1 ? "" : "s"} · ${q.amount}</div>
                        {q.message && <div className="text-slate-300 text-sm mt-1">{q.message}</div>}
                      </div>
                      {job.status === "open" && (
                        <button onClick={() => accept(job, q.quote_id)} disabled={busy === q.quote_id} className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400 !py-1.5 !px-3 text-xs" data-testid={`ct-accept-${q.quote_id}`}>
                          {busy === q.quote_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Accept
                        </button>
                      )}
                      {job.accepted_quote_id === q.quote_id && <span className="text-emerald-300 text-xs fl-mono uppercase tracking-widest">Winner</span>}
                    </div>
                  </div>
                ))}
              </div>
              {job.status === "assigned" && (
                <button onClick={() => complete(job)} disabled={busy === job.job_id} className="fl-btn mt-3 !py-2 !px-3 text-xs" data-testid={`ct-complete-${job.job_id}`}>
                  {busy === job.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark complete
                </button>
              )}
              {job.status === "completed" && !job.review && <ReviewForm jobId={job.job_id} onDone={load} />}
              {job.review && <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm"><Star className="w-4 h-4 inline" /> You rated this {job.review.rating}/5. Thanks for reviewing!</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewForm({ jobId, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/contractors/jobs/${jobId}/review`, { rating, text }, { headers: authHeaders() }); onDone?.(); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 p-3 rounded-xl bg-black/40 border border-white/5" data-testid={`ct-review-${jobId}`}>
      <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Leave a review</div>
      <div className="mt-2 flex items-center gap-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setRating(n)} className={`w-8 h-8 grid place-items-center ${n <= rating ? "text-emerald-300" : "text-white/20"}`} data-testid={`ct-review-star-${n}`}>
            <Star className={`w-6 h-6 ${n <= rating ? "fill-emerald-300" : ""}`} />
          </button>
        ))}
      </div>
      <textarea className="fl-input w-full mt-2" rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Anything future homeowners should know?" maxLength={600} data-testid={`ct-review-text-${jobId}`} />
      <button onClick={submit} disabled={busy} className="fl-btn mt-2 !py-2 !px-3 text-xs !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid={`ct-review-submit-${jobId}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit review
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// PRO HOME — available jobs, my quotes, my stats
// ────────────────────────────────────────────────────────────
function ProHome({ me, onReload }) {
  const [tab, setTab] = useState("available");
  const [stats, setStats] = useState(null);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/contractors/pros/mine", { headers: authHeaders() }),
        api.get("/contractors/jobs/available", { headers: authHeaders() }),
      ]);
      setStats(s.data); setAvailable(a.data.jobs || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load."); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  if (loading) return <div className="text-white/40 fl-mono text-xs py-8">// Loading your pro workspace…</div>;

  return (
    <div className="space-y-4" data-testid="ct-pro-home">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Jobs open to quote", value: available.length },
          { label: "My open quotes", value: stats?.stats.open_quotes || 0 },
          { label: "Jobs won", value: stats?.stats.jobs_assigned || 0 },
          { label: "Rating", value: stats?.stats.reviews_count ? `${stats.stats.rating_avg.toFixed(1)} ★ (${stats.stats.reviews_count})` : "—" },
        ].map((s, i) => (
          <div key={i} className="fl-card p-3 bg-white/[0.02] border-white/5">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">{s.label}</div>
            <div className="fl-display text-xl font-black text-white mt-1">{s.value}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[
          { id: "available", label: "Available jobs", icon: Sparkles },
          { id: "assigned", label: "My assigned jobs", icon: Award },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${tab === t.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`ct-pro-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {err && <div className="text-red-300 text-sm">{err}</div>}
      {tab === "available" && (available.length ? available.map(j => <AvailableJobRow key={j.job_id} job={j} onQuoted={loadAll} />) : <div className="fl-card p-6 bg-white/[0.02] border-white/5 text-slate-400 text-sm">No open jobs in your ZIPs and trades yet. Check back soon.</div>)}
      {tab === "assigned" && ((stats?.assigned_jobs || []).length ? stats.assigned_jobs.map(j => <AssignedJobRow key={j.job_id} job={j} />) : <div className="fl-card p-6 bg-white/[0.02] border-white/5 text-slate-400 text-sm">No assigned jobs yet.</div>)}
    </div>
  );
}

function AvailableJobRow({ job, onQuoted }) {
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [days, setDays] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!amount || !days) { setErr("Amount and timeline required."); return; }
    setErr(""); setBusy(true);
    try {
      await api.post(`/contractors/jobs/${job.job_id}/quote`, { amount: parseInt(amount, 10), timeline_days: parseInt(days, 10), message }, { headers: authHeaders() });
      setShowForm(false); setAmount(""); setDays(""); setMessage(""); onQuoted?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Quote failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/5" data-testid={`ct-avail-${job.job_id}`}>
      <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">// {job.trade} · ZIP {job.zip_code} · {job.severity}</div>
      <div className="text-white text-sm mt-1">{job.description}</div>
      {job.photos?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {job.photos.map(p => <img key={p} src={resolveAudio(p)} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/10" />)}
        </div>
      )}
      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="fl-btn mt-3 !py-2 !px-3 text-xs !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid={`ct-quote-btn-${job.job_id}`}><DollarSign className="w-3.5 h-3.5" /> Submit quote</button>
      ) : (
        <div className="mt-3 grid sm:grid-cols-3 gap-2">
          <input className="fl-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount ($)" data-testid={`ct-quote-amt-${job.job_id}`} />
          <input className="fl-input" type="number" value={days} onChange={e => setDays(e.target.value)} placeholder="Timeline (days)" data-testid={`ct-quote-days-${job.job_id}`} />
          <input className="fl-input" value={message} onChange={e => setMessage(e.target.value)} placeholder="Short note (optional)" maxLength={600} data-testid={`ct-quote-msg-${job.job_id}`} />
          <div className="sm:col-span-3 flex items-center gap-2">
            <button onClick={submit} disabled={busy} className="fl-btn !py-2 !px-3 text-xs !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid={`ct-quote-submit-${job.job_id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send quote
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs text-white/50 hover:text-white">Cancel</button>
            {err && <div className="text-red-300 text-xs">{err}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function AssignedJobRow({ job }) {
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/5" data-testid={`ct-assigned-${job.job_id}`}>
      <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">// {job.trade} · ZIP {job.zip_code} · {job.status}</div>
      <div className="text-white text-sm mt-1">{job.description}</div>
      {job.status === "completed" && job.review && (
        <div className="mt-2 text-emerald-200 text-xs"><Star className="w-3 h-3 inline fill-emerald-300" /> {job.review.rating}/5 — &ldquo;{job.review.text}&rdquo;</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// MEASURE TAPE — kid-friendly digital tape for real home projects
// ────────────────────────────────────────────────────────────
function MeasureTape() {
  const [mode, setMode] = useState("log");
  return (
    <div className="fl-card p-6 bg-white/[0.02] border-emerald-400/20" data-testid="ct-measure">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400">// Project measure tape</div>
          <h2 className="fl-display text-2xl font-black text-white mt-1">Big kids and grown-ups. Zero math.</h2>
          <p className="text-slate-400 text-sm mt-1">Say the number out loud, or snap a photo. We keep the running list for every room.</p>
        </div>
        <Ruler className="w-8 h-8 text-emerald-300 shrink-0" />
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {[
          { id: "log", label: "Measurement log" },
          { id: "photo", label: "Photo measure" },
        ].map(t => (
          <button key={t.id} onClick={() => setMode(t.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${mode === t.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`ct-measure-mode-${t.id}`}>{t.label}</button>
        ))}
      </div>
      {mode === "log" ? <MeasurementLog /> : <PhotoMeasure />}
    </div>
  );
}

// Parse strings like: "eight feet six inches", "8'6\"", "8 ft 6 in",
// "102 inches", "2.5 meters", "34cm". Returns { inches } or null.
function parseMeasurement(raw) {
  if (!raw) return null;
  let s = String(raw).trim().toLowerCase().replace(/,/g, ".").replace(/&/g, " and ");
  // Number words → digits (0-20 + common)
  const words = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100, "and": " " };
  s = s.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|and)\b/g, w => (words[w] === undefined ? w : String(words[w])));
  // Half / quarter
  s = s.replace(/half/g, "0.5").replace(/quarter/g, "0.25").replace(/three quarters?/g, "0.75");
  // Foot/inch symbols → words
  s = s.replace(/′|'|ft\b|feet\b|foot\b/g, " feet ");
  s = s.replace(/″|"|in\b|inches\b|inch\b/g, " inches ");
  s = s.replace(/cm\b|centimeter[s]?\b/g, " cm ");
  s = s.replace(/m\b|meter[s]?/g, " m ");
  s = s.replace(/mm\b|millimeter[s]?\b/g, " mm ");
  s = s.replace(/\s+/g, " ").trim();

  // Feet + inches pattern
  const ftIn = s.match(/([\d.]+)\s*feet\s*([\d.]+)?\s*(?:inches)?/);
  if (ftIn) {
    const ft = parseFloat(ftIn[1]) || 0;
    const inches = parseFloat(ftIn[2] || "0") || 0;
    if (ft > 0 || inches > 0) return { inches: ft * 12 + inches };
  }
  // Feet only
  const ft = s.match(/([\d.]+)\s*feet/);
  if (ft) return { inches: parseFloat(ft[1]) * 12 };
  // Inches only
  const inn = s.match(/([\d.]+)\s*inches/);
  if (inn) return { inches: parseFloat(inn[1]) };
  // Metric
  const cm = s.match(/([\d.]+)\s*cm/);
  if (cm) return { inches: parseFloat(cm[1]) / 2.54 };
  const mm = s.match(/([\d.]+)\s*mm/);
  if (mm) return { inches: parseFloat(mm[1]) / 25.4 };
  const meters = s.match(/([\d.]+)\s*m\b/);
  if (meters) return { inches: parseFloat(meters[1]) * 39.3701 };
  // Bare number → assume inches
  const bare = s.match(/^([\d.]+)$/);
  if (bare) return { inches: parseFloat(bare[1]) };
  return null;
}

function formatMeasurement(inches, unit) {
  if (!inches || isNaN(inches)) return "—";
  if (unit === "cm") return `${(inches * 2.54).toFixed(1)} cm`;
  if (unit === "m") return `${(inches * 2.54 / 100).toFixed(2)} m`;
  // Default: feet + inches
  const ft = Math.floor(inches / 12);
  const rem = inches - ft * 12;
  if (ft === 0) return `${rem.toFixed(1)}"`;
  return `${ft}' ${rem.toFixed(1)}"`;
}

// Persistent per-project measurement log with voice-in via Whisper (reuses
// the Astro oracle endpoint isn't right — we transcribe locally with the
// browser's SpeechRecognition when available, else offer a text input).
function MeasurementLog() {
  const LS_KEY = "ct_measurement_projects";
  const [projects, setProjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); }
    catch { return []; }
  });
  const [activeId, setActiveId] = useState(() => projects[0]?.id || null);
  const [newProjectName, setNewProjectName] = useState("");
  const [unit, setUnit] = useState("ft"); // ft | cm | m

  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(projects)); }, [projects]);

  const active = projects.find(p => p.id === activeId);
  const addProject = () => {
    const name = newProjectName.trim();
    if (!name) return;
    const p = { id: `pj_${Date.now().toString(36)}`, name, created_at: new Date().toISOString(), items: [] };
    setProjects(prev => [p, ...prev]); setActiveId(p.id); setNewProjectName("");
  };
  const deleteProject = (id) => {
    if (!window.confirm("Delete this project and all its measurements?")) return;
    setProjects(prev => prev.filter(p => p.id !== id));
    if (activeId === id) setActiveId(null);
  };
  const addItem = (label, inches) => {
    if (!active || !inches) return;
    const item = { id: `m_${Date.now().toString(36)}`, label: label || `Measurement ${active.items.length + 1}`, inches, created_at: new Date().toISOString() };
    setProjects(prev => prev.map(p => p.id === active.id ? { ...p, items: [item, ...p.items] } : p));
  };
  const deleteItem = (itemId) => {
    setProjects(prev => prev.map(p => p.id === active.id ? { ...p, items: p.items.filter(i => i.id !== itemId) } : p));
  };

  const total = (active?.items || []).reduce((s, i) => s + (i.inches || 0), 0);

  return (
    <div className="mt-4 space-y-3" data-testid="ct-measure-log">
      {/* Project switcher */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[180px] flex items-center gap-2">
          <input
            className="fl-input flex-1"
            value={newProjectName}
            onChange={e => setNewProjectName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addProject(); } }}
            placeholder="New project (e.g. Kitchen remodel, Bathroom paint)"
            maxLength={60}
            data-testid="ct-measure-project-name"
          />
          <button onClick={addProject} className="fl-btn ghost !py-2 !px-3 text-xs" data-testid="ct-measure-project-add"><Plus className="w-3.5 h-3.5" /> New</button>
        </div>
        <div className="flex items-center gap-1">
          {[
            { id: "ft", label: "ft/in" },
            { id: "cm", label: "cm" },
            { id: "m",  label: "m" },
          ].map(u => (
            <button key={u.id} onClick={() => setUnit(u.id)} className={`px-2.5 py-1 rounded text-xs font-semibold ${unit === u.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60"}`} data-testid={`ct-measure-unit-${u.id}`}>{u.label}</button>
          ))}
        </div>
      </div>
      {projects.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ct-measure-projects">
          {projects.map(p => (
            <button key={p.id} onClick={() => setActiveId(p.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${activeId === p.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`ct-measure-project-${p.id}`}>
              {p.name} <span className="opacity-60">{p.items.length}</span>
            </button>
          ))}
        </div>
      )}

      {!active ? (
        <div className="fl-card p-6 bg-black/40 border-white/5 text-slate-400 text-sm text-center" data-testid="ct-measure-empty">
          <Ruler className="w-10 h-10 text-emerald-300 mx-auto opacity-60" />
          <div className="mt-3">Type a project name above and hit New — every room gets its own measurement page.</div>
        </div>
      ) : (
        <div className="fl-card p-4 bg-black/40 border-emerald-400/20" data-testid="ct-measure-active">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">// Active project</div>
              <div className="text-white text-lg font-bold">{active.name}</div>
            </div>
            <button onClick={() => deleteProject(active.id)} className="text-white/40 hover:text-red-400 text-xs" data-testid="ct-measure-project-delete"><Trash2 className="w-4 h-4" /></button>
          </div>
          <QuickAdd unit={unit} onAdd={addItem} />
          {active.items.length > 0 ? (
            <>
              <div className="mt-4 divide-y divide-white/5 border border-white/5 rounded-xl overflow-hidden" data-testid="ct-measure-items">
                {active.items.map(it => (
                  <div key={it.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/[0.02] hover:bg-white/[0.05]" data-testid={`ct-measure-item-${it.id}`}>
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{it.label}</div>
                      <div className="text-white/40 text-[10px] fl-mono">{new Date(it.created_at).toLocaleString()}</div>
                    </div>
                    <div className="fl-display text-xl font-black text-emerald-300 tabular-nums shrink-0">{formatMeasurement(it.inches, unit)}</div>
                    <button onClick={() => deleteItem(it.id)} className="text-white/40 hover:text-red-400 shrink-0" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300">// Running total</div>
                <div className="fl-display text-2xl font-black text-emerald-300 tabular-nums" data-testid="ct-measure-total">{formatMeasurement(total, unit)}</div>
              </div>
            </>
          ) : (
            <div className="mt-4 text-slate-400 text-sm">No measurements yet. Add your first one below.</div>
          )}
        </div>
      )}

      {/* How-to strip */}
      <div className="rounded-xl bg-black/30 border border-white/5 p-3 text-xs text-white/60 fl-mono">
        <Info className="w-3.5 h-3.5 inline text-emerald-300 mr-1" />
        Tap <strong className="text-white">🎙 Say it</strong> then speak: <em>"eight feet six inches"</em>, <em>"32 inches"</em>, or <em>"2 meters"</em>. Or just type. Both work.
      </div>
    </div>
  );
}

function QuickAdd({ unit, onAdd }) {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [recording, setRecording] = useState(false);
  const [err, setErr] = useState("");
  const recogRef = useRef(null);

  useEffect(() => {
    const parsed = parseMeasurement(text);
    setPreview(parsed?.inches || null);
  }, [text]);

  const startRec = () => {
    setErr("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setErr("Voice input isn't supported on this browser. Type the number instead.");
      return;
    }
    try {
      const r = new SR();
      r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
      r.onresult = (e) => {
        const said = e.results[0][0].transcript;
        setText(t => (t ? t + " " : "") + said);
      };
      r.onerror = () => setRecording(false);
      r.onend = () => setRecording(false);
      recogRef.current = r;
      r.start(); setRecording(true);
    } catch (_) {
      setErr("Voice input failed to start. Type the number instead.");
    }
  };
  const stopRec = () => { try { recogRef.current?.stop(); } catch (_) {} setRecording(false); };

  const submit = () => {
    const parsed = parseMeasurement(text);
    if (!parsed || parsed.inches <= 0) { setErr("Try again — say something like 'eight feet six inches' or '32 inches'."); return; }
    onAdd(label.trim() || null, parsed.inches);
    setLabel(""); setText(""); setPreview(null); setErr("");
  };

  return (
    <div className="mt-4 p-3 rounded-xl bg-black/40 border border-white/5" data-testid="ct-quickadd">
      <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60">// Add a measurement</div>
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        <input
          className="fl-input"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Label (e.g. North wall, Cabinet door)"
          maxLength={60}
          data-testid="ct-quickadd-label"
        />
        <div className="flex items-center gap-2">
          <input
            className="fl-input flex-1"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder='"8 feet 6 inches" or 32"'
            data-testid="ct-quickadd-value"
          />
          {!recording ? (
            <button onClick={startRec} className="fl-btn !bg-emerald-500 !text-black hover:!bg-emerald-400 !py-2 !px-3 text-xs" data-testid="ct-quickadd-mic"><Mic /> Say it</button>
          ) : (
            <button onClick={stopRec} className="fl-btn !bg-red-500 hover:!bg-red-400 animate-pulse !py-2 !px-3 text-xs" data-testid="ct-quickadd-mic-stop"><Mic /> Stop</button>
          )}
        </div>
      </div>
      {preview !== null && (
        <div className="mt-2 flex items-center justify-between p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="ct-quickadd-preview">
          <div className="text-emerald-300 text-xs fl-mono uppercase tracking-widest">// preview</div>
          <div className="fl-display text-2xl font-black text-emerald-300 tabular-nums">{formatMeasurement(preview, unit)}</div>
        </div>
      )}
      {err && <div className="text-red-300 text-sm mt-2" data-testid="ct-quickadd-err">{err}</div>}
      <button onClick={submit} className="fl-btn mt-3 !bg-emerald-500 !text-black hover:!bg-emerald-400 w-full sm:w-auto" data-testid="ct-quickadd-submit"><Plus className="w-4 h-4" /> Add to list</button>
    </div>
  );
}



// Photo measure — user snaps a photo with a reference object (credit card).
// They tap the two endpoints of the reference, we compute px/cm, then they
// tap the two endpoints of the actual object → real distance.
function PhotoMeasure() {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [refCm, setRefCm] = useState(8.56);
  const [step, setStep] = useState("upload"); // upload | ref | measure | done
  const [pts, setPts] = useState([]); // [refA, refB, objA, objB]
  const [pxPerCm, setPxPerCm] = useState(0);
  const [unit, setUnit] = useState("cm");

  const onFile = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { setImg(image); setStep("ref"); setPts([]); };
    image.src = url;
  };

  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const c = canvasRef.current;
    const maxW = c.parentElement.clientWidth || 600;
    const scale = Math.min(1, maxW / img.width);
    c.width = img.width * scale;
    c.height = img.height * scale;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    // Draw points and lines
    pts.forEach((p, i) => {
      ctx.beginPath(); ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = i < 2 ? "#ef4444" : "#3b82f6"; ctx.fill();
      ctx.font = "bold 12px sans-serif"; ctx.fillStyle = "#fff";
      const label = i < 2 ? `R${i + 1}` : `O${i - 1}`;
      ctx.fillText(label, p.x + 10, p.y - 8);
    });
    if (pts.length >= 2) {
      ctx.strokeStyle = "#ef4444"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); ctx.stroke();
    }
    if (pts.length === 4) {
      ctx.strokeStyle = "#3b82f6"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(pts[2].x, pts[2].y); ctx.lineTo(pts[3].x, pts[3].y); ctx.stroke();
    }
  }, [img, pts]);

  const handleClick = (e) => {
    if (!img || step === "done") return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const next = [...pts, { x, y }];
    if (next.length === 2) {
      // Compute px/cm from the reference line
      const dpx = Math.hypot(next[1].x - next[0].x, next[1].y - next[0].y);
      setPxPerCm(dpx / (refCm || 1));
      setStep("measure");
    }
    if (next.length === 4) setStep("done");
    setPts(next);
  };

  const objectDist = pts.length === 4 ? Math.hypot(pts[3].x - pts[2].x, pts[3].y - pts[2].y) : 0;
  const cm = pxPerCm > 0 ? objectDist / pxPerCm : 0;
  const value = unit === "cm" ? cm : cm / 2.54;

  return (
    <div className="mt-4 space-y-3" data-testid="ct-photo-measure">
      {step === "upload" && (
        <div className="rounded-xl bg-black/40 border border-emerald-400/30 p-6 text-center" data-testid="ct-photo-upload-step">
          <Camera className="w-10 h-10 text-emerald-300 mx-auto" />
          <h3 className="fl-display text-lg font-bold text-white mt-3">Snap a photo — put a coin or credit card next to the thing you&apos;re measuring.</h3>
          <p className="text-slate-400 text-sm mt-1">Anything you know the size of works. It&apos;s our &quot;ruler stand-in&quot;.</p>
          <label className="fl-btn mt-4 !bg-emerald-500 !text-black hover:!bg-emerald-400 cursor-pointer" data-testid="ct-photo-upload-btn">
            <Camera className="w-4 h-4" /> Take / choose photo
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => onFile(e.target.files?.[0])} data-testid="ct-photo-file" />
          </label>
        </div>
      )}
      {img && (
        <>
          <div className="rounded-xl bg-black/60 border border-white/10 p-2">
            <canvas ref={canvasRef} onClick={handleClick} onTouchEnd={handleClick} className="w-full cursor-crosshair rounded-lg" data-testid="ct-photo-canvas" />
          </div>
          <div className="fl-card p-3 bg-black/40 border-white/5" data-testid="ct-photo-hint">
            {step === "ref" && (
              <>
                <div className="text-white text-sm font-semibold">Step 1 — Tap both ends of your reference object.</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Real size (cm)</span>
                  <input className="fl-input !w-24" type="number" step="0.01" value={refCm} onChange={e => setRefCm(parseFloat(e.target.value) || 0)} data-testid="ct-photo-refcm" />
                  <span className="text-xs text-white/50">credit card = 8.56 · quarter = 2.43 · dollar bill = 15.60</span>
                </div>
                <div className="text-xs text-white/40 fl-mono mt-1">Tapped: {pts.length}/2</div>
              </>
            )}
            {step === "measure" && (
              <>
                <div className="text-white text-sm font-semibold">Step 2 — Tap both ends of the thing you&apos;re measuring.</div>
                <div className="text-xs text-white/40 fl-mono mt-1">Tapped: {pts.length - 2}/2</div>
              </>
            )}
            {step === "done" && (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">Measured length</div>
                  <div className="fl-display text-3xl font-black text-emerald-300 tabular-nums" data-testid="ct-photo-result">{value.toFixed(2)} {unit === "cm" ? "cm" : "inches"}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setUnit("cm")} className={`px-2 py-1 rounded text-xs font-semibold ${unit === "cm" ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60"}`}>cm</button>
                  <button onClick={() => setUnit("in")} className={`px-2 py-1 rounded text-xs font-semibold ${unit === "in" ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60"}`}>inches</button>
                </div>
              </div>
            )}
            <button onClick={() => { setPts([]); setStep(img ? "ref" : "upload"); setPxPerCm(0); }} className="fl-btn ghost !py-1.5 !px-3 text-xs mt-3" data-testid="ct-photo-reset">Start over</button>
          </div>
        </>
      )}
    </div>
  );
}
